package one.healthium.uscreen.ui.layout

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.SubcomposeLayout
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.dp

/**
 * Variant A: SubcomposeLayout-based adaptive scroll.
 *
 * Pass 1 — measures all blocks with flex spacers treated as fixed (fallbackFlexDp).
 * If total height > available → renders scrollable Column with fixed spacers.
 * If total height ≤ available → renders Column with weight() for flex spacers.
 *
 * scrollable decision is locked after first layout to prevent instability
 * when keyboard changes available height.
 */
@Composable
fun SubcomposeBlockLayout(
    modifier: Modifier = Modifier,
    bottomPadding: Int = 0,
    content: @Composable ColumnScope.(scrollable: Boolean, measureOnly: Boolean) -> Unit,
) {
    val scrollState = rememberScrollState()
    val scrollDecision = remember { mutableStateOf<Boolean?>(null) }

    SubcomposeLayout(
        modifier = modifier.testTag("uscreen_subcompose_layout"),
    ) { constraints ->
        val maxHeight = constraints.maxHeight
        val maxWidth = constraints.maxWidth
        val bottomPaddingPx = bottomPadding.dp.roundToPx()

        val needsScroll = scrollDecision.value ?: run {
            // First layout: measure to determine if scroll needed
            val measurePlaceable = subcompose("measure") {
                Column {
                    content(true, true)
                }
            }.first().measure(Constraints(maxWidth = maxWidth))

            val result = measurePlaceable.height + bottomPaddingPx > maxHeight
            scrollDecision.value = result
            result
        }

        // Compose actual content with locked scroll decision
        val finalPlaceable = subcompose("final") {
            if (needsScroll) {
                Column(modifier = Modifier.verticalScroll(scrollState)) {
                    content(true, false)
                }
            } else {
                Column {
                    content(false, false)
                }
            }
        }.first().measure(
            Constraints(
                maxWidth = maxWidth,
                minWidth = maxWidth,
                maxHeight = maxHeight,
                minHeight = if (needsScroll) 0 else maxHeight,
            ),
        )

        layout(maxWidth, maxHeight) {
            finalPlaceable.place(0, 0)
        }
    }
}
